import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Phone, Video, PhoneOff } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IncomingCallOverlay = () => {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [incomingCall, setIncomingCall] = useState(null);
  const pollRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    const headers = getAuthHeaders();

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/calls/incoming`, { headers });
        if (res.data?.length > 0) {
          const call = res.data[0];
          setIncomingCall((prev) => {
            if (prev?.id === call.id) return prev;
            return call;
          });
        } else {
          setIncomingCall(null);
        }
      } catch {
        // Ignore polling errors
      }
    };

    poll();
    pollRef.current = setInterval(poll, 3000);
    return () => clearInterval(pollRef.current);
  }, [user]);

  // Play ringtone when there's an incoming call
  useEffect(() => {
    if (incomingCall) {
      try {
        const audio = new Audio("/ringtone.wav");
        audio.loop = true;
        audio.volume = 0.7;
        audioRef.current = audio;
        audio.play().catch(() => {
          // Autoplay blocked — retry on next user interaction
          const unlock = () => {
            if (audioRef.current === audio) audio.play().catch(() => {});
            document.removeEventListener("click", unlock);
            document.removeEventListener("touchstart", unlock);
          };
          document.addEventListener("click", unlock, { once: true });
          document.addEventListener("touchstart", unlock, { once: true });
        });
      } catch { /* ignore */ }
    } else {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    }
    return () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } };
  }, [incomingCall?.id]);

  const answerCall = () => {
    if (!incomingCall) return;
    const callerId = incomingCall.caller_id;
    setIncomingCall(null);
    navigate(`/messages/${callerId}?answerCall=${incomingCall.id}`);
  };

  const rejectCall = async () => {
    if (!incomingCall) return;
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/calls/${incomingCall.id}/reject`, {}, { headers });
      toast.info("Call rejected");
    } catch {
      // ignore
    }
    setIncomingCall(null);
  };

  if (!incomingCall) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="incoming-call-overlay">
      <div className="bg-[#15151F] border border-violet-500/30 rounded-2xl p-8 w-[340px] shadow-2xl shadow-violet-500/10 text-center">
        {/* Caller avatar */}
        <div className="mb-4 relative mx-auto w-20 h-20">
          <div className="absolute inset-0 rounded-full bg-violet-500/20 animate-ping" />
          {incomingCall.caller_avatar ? (
            <img src={incomingCall.caller_avatar} alt="" className="w-20 h-20 rounded-full object-cover relative z-10 border-2 border-violet-500/50" />
          ) : (
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-violet-600 to-purple-700 flex items-center justify-center relative z-10 border-2 border-violet-500/50">
              <span className="text-2xl font-bold text-white">{(incomingCall.caller_name || "?")[0]?.toUpperCase()}</span>
            </div>
          )}
        </div>

        <h3 className="text-white font-semibold text-lg mb-1" data-testid="caller-name">{incomingCall.caller_name || "Unknown"}</h3>
        <p className="text-gray-400 text-sm mb-6">
          Incoming {incomingCall.type === "video" ? "video" : "voice"} call...
        </p>

        {/* Action buttons */}
        <div className="flex items-center justify-center gap-8">
          <button
            onClick={rejectCall}
            className="w-14 h-14 rounded-full bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 flex items-center justify-center transition-all hover:scale-110"
            data-testid="reject-call-btn"
          >
            <PhoneOff className="w-6 h-6 text-red-400" />
          </button>
          <button
            onClick={answerCall}
            className="w-14 h-14 rounded-full bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 flex items-center justify-center transition-all hover:scale-110 animate-pulse"
            data-testid="accept-call-btn"
          >
            {incomingCall.type === "video" ? (
              <Video className="w-6 h-6 text-green-400" />
            ) : (
              <Phone className="w-6 h-6 text-green-400" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IncomingCallOverlay;
